import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import validate from 'validate.js';

// Externals
import classNames from 'classnames';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import compose from 'recompose/compose';
import { connect } from "react-redux";
import _ from 'underscore';
import schema from './schema';

// Material helpers
import { withStyles } from '@material-ui/core';

// Material components
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Dialog, 
  DialogActions, 
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormGroup,
  FormControlLabel
} from '@material-ui/core';

// Shared components
import { 
  Portlet, 
  PortletContent, 
  PortletHeader, 
  PortletLabel, 
  PortletToolbar,
  SearchInput,
  Status,
 } from 'components';

import api from '../../../../api';

// Component styles
import styles from './styles';

const statusColors = {
  Active: 'success',
  Suspend: 'danger',
  Initial: 'neutral',
};

class AccountsTable extends Component {
  state = {
    rowsPerPage: 10,
    page: 0,
    accounts: [],
    searchKey: '',
    dialogOpen:'',
    values: {
      name: '',
      email: '',
      password: ''
    },
    touched: {
      name: false,
      email: false,
      password: false
    },
    errors: {
      name: null,
      email: null,
      password: null
    },
    isValid: false,
  };

  componentDidMount () {
    this.updateAccountsTable();
  }

  updateAccountsTable = async () => {
    let {data} = await api.requestAccounts();
    console.log(data);
    if(data && _.isArray(data.accounts)) {
      this.setState({accounts:data.accounts});
    }
  }

  validateForm = _.debounce(() => {
    const { values } = this.state;

    const newState = { ...this.state };
    const errors = validate(values, schema);

    newState.errors = errors || {};
    newState.isValid = errors ? false : true;

    this.setState(newState);
  }, 300);

  handleChangePage = (event, page) => {
    this.setState({ page });
  };

  handleChangeRowsPerPage = event => {
    this.setState({ rowsPerPage: event.target.value });
  };

  handleDeleteAccount = async (email) => {
    await api.deleteAccount({email})
    this.updateAccountsTable()
  }

  handleSearchAccount = (searchKey) => {
    this.setState({searchKey});
  }

  handleClickOpenAccountDialog = () => {
    this.setState({dialogOpen:'addAccount'});
  }

  handleFieldChange = (field, value) => {
    const newState = { ...this.state };
    newState.values[field] = value;
    newState.touched[field] = true;
    this.setState(newState, this.validateForm);
  };

  handleCloseDialog = () => {
    this.setState({
      dialogOpen:'',
      values: {
        name: '',
        email: '',
        password: ''
      },
      touched: {
        name: false,
        email: false,
        password: false
      },
      errors: {
        name: null,
        email: null,
        password: null
      },
      isValid: false,
    });
  }

  handleAddAccount = async () => {
    try {
      const { name, email, password } = this.state.values;
      await api.addAccount({name, email, password})
      this.updateAccountsTable();
      this.handleCloseDialog();
    } catch (error) {
      console.error(error);
    }
  };

  renderAddAccountDialog = () => {
    const {classes} = this.props;
    const { values, touched, errors, isValid, dialogOpen } = this.state;
    const showNameError = touched.name && errors.name;
    const showEmailError = touched.email && errors.email;
    const showPasswordError = touched.password && errors.password;
    return (
      <Dialog open={dialogOpen === 'addAccount'} onClose={this.handleCloseDialog} className={classes.cidDialog}>
        <DialogTitle id="form-dialog-title">Add New User Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please specify User Name, Password, Role and Email
          </DialogContentText>
          <TextField
            className={classes.textField}
            label="Name"
            name="name"
            onChange={event =>
              this.handleFieldChange('name', event.target.value)
            }
            value={values.name}
            variant="outlined"
            placeholder="example-customer-id"
            InputLabelProps={{
              shrink: true,
            }}
          />
          {showNameError && (
            <Typography
              className={classes.fieldError}
              variant="body2"
            >
              {errors.name[0]}
            </Typography>
          )}
          <TextField
            className={classes.textField}
            label="Email"
            name="email"
            onChange={event =>
              this.handleFieldChange('email', event.target.value)
            }
            value={values.email}
            variant="outlined"
            placeholder="example-customer-id"
            InputLabelProps={{
              shrink: true,
            }}
          />
          {showEmailError && (
            <Typography
              className={classes.fieldError}
              variant="body2"
            >
              {errors.email[0]}
            </Typography>
          )}
          <TextField
            className={classes.textField}
            label="Password"
            name="password"
            onChange={event =>
              this.handleFieldChange('password', event.target.value)
            }
            value={values.password}
            variant="outlined"
            placeholder="example-customer-id"
            InputLabelProps={{
              shrink: true,
            }}
          />
          {showPasswordError && (
            <Typography
              className={classes.fieldError}
              variant="body2"
            >
              {errors.password[0]}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={this.handleAddAccount} disabled={!isValid} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    )
  }


  render() {
    const { classes, className} = this.props;
    const { rowsPerPage, page, accounts, searchKey } = this.state;
    const displayAccounts = accounts.filter(account => {
      if(account.name.includes(searchKey))
        return true;
      if(account.email.includes(searchKey))
        return true;
      return false;
    })
    const accountTotal = displayAccounts.length;
    const rootClassName = classNames(classes.root, className);

    return (
      <Portlet className={rootClassName}>
        {this.renderAddAccountDialog()}
        <PortletHeader noDivider>
          <PortletLabel
            subtitle={`${accountTotal} in total`}
            title="Accounts"
          />
          <SearchInput
            className={classes.searchInput}
            placeholder="Search account"
            onChange={event =>
              this.handleSearchAccount(event.target.value)
            }
          />
          <PortletToolbar>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Button
                    className={classes.newEntryButton}
                    color="primary"
                    size="small"
                    variant="outlined"
                    onClick={this.handleClickOpenAccountDialog}
                  >
                    New User Account
                  </Button>
                }
              />
            </FormGroup>
          </PortletToolbar>
        </PortletHeader>
        <PortletContent noPadding>
          <PerfectScrollbar>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    Name
                  </TableCell>
                  <TableCell align="left">Role</TableCell>
                  <TableCell align="left">Email</TableCell>
                  <TableCell align="left">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayAccounts
                  .slice(page*rowsPerPage, (page+1)*rowsPerPage)
                  .map(account => (
                    <TableRow
                      className={classes.tableRow}
                      hover
                      key={account.name}
                    >
                      <TableCell className={classes.tableCell} align="left">
                        <div className={classes.tableCellInner}>
                          <Link to="#">
                            {account.name}
                          </Link>
                        </div>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <div className={classes.statusWrapper}>
                          <Status
                            className={classes.status}
                            color={statusColors[account.status]}
                            size="sm"
                          />
                          {account.role}
                        </div>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {account.email}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Button
                          color="primary"
                          size="small"
                          variant="outlined"
                          onClick={()=>this.handleDeleteAccount(account.email)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </PerfectScrollbar>
          <TablePagination
            backIconButtonProps={{
              'aria-label': 'Previous Page'
            }}
            component="div"
            count={accounts.length}
            nextIconButtonProps={{
              'aria-label': 'Next Page'
            }}
            onChangePage={this.handleChangePage}
            onChangeRowsPerPage={this.handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </PortletContent>
      </Portlet>
    );
  }
}

const mapStateToProps = state => ({
  auth: state.auth,
});

AccountsTable.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  onShowDetails: PropTypes.func,
  auth: PropTypes.object.isRequired,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps,null)
)(AccountsTable);